import { observer } from "mobx-react-lite"
import Layout from "../../components/layout/Layout"
import { Button, Flex, message, DatePicker, Tabs } from "antd";
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useStore } from "../../stores/root-store-context";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import TabPane from "antd/es/tabs/TabPane";
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { TimeReport } from "../../api/TimeReportsApiClient";
import { useTranslation } from "react-i18next";
import { AddTimeReport } from "./AddTimeReport";
import { EditTimeReport } from "./EditTimeReport";
import ProjectsTimeReports from "./ProjectsTimeReports";
import EventPage from "../Events/EventPage";
import { CalendarEvent } from "../../api/EventsApiClient";
import UserTaskPage from "../UserTasks/UserTaskPage";
import ByUser from "./ByUser";
import EnhancedCalendar from "../Extended Calendar/EnhancedCalendar";
import { MyTime } from "./MyTime";
import { Roles } from "../../enums/Roles";

dayjs.extend(duration);

const { RangePicker } = DatePicker;

const TimeReports = observer(() => {
  const { applicationStore, timeReportStore, employeesStore, projectsStore, eventStore } = useStore();
  const navigate = useNavigate();
  const { tabKey } = useParams<{ tabKey: string }>();
  const { t } = useTranslation();

  const [refresh, setRefresh] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);

  const [activeProjectTabKey, setActiveProjectTabKey] = useState('byProjects');
  const [activeUserTabKey, setActiveUserTabKey] = useState('ByUser');

  const [editTimeReport, setEditTimeReport] = useState<TimeReport>();
  const [logUserId, setLogUserId] = useState<string>("");
  const [logProjectId, setLogProjectId] = useState<string>("");
  const [isChangeUser, setIsChangeUser] = useState<boolean>(true);

  const [events, setEvents] = useState<any[]>([]);
  const [isAllTimeChecked, setIsAllTimeChecked] = useState(false);

  const tenant = applicationStore.company?.Tenant;

  const isChief = employeesStore.users.some(user => user.chiefId === applicationStore.user.id);

  const showModal = () => setIsModalOpen(true);
  const hideModal = () => setIsModalOpen(false);

  const showModalEdit = () => setIsModalOpenEdit(true);
  const hideModalEdit = () => {
    setIsChangeUser(true);
    setIsModalOpenEdit(false);
  };

  const resetToCurrentMonth = () => {
    timeReportStore.setStartDate(dayjs().startOf("month").local());
    timeReportStore.setEndDate(dayjs().endOf("month").local());
  };

  const setFullYearRange = () => {
    timeReportStore.setStartDate(dayjs().startOf('year').local());
    timeReportStore.setEndDate(dayjs().endOf('year').local());
  };

  const handleUserTabChange = (key: string) => setActiveUserTabKey(key);
  const handleProjectTabChange = (key: string) => setActiveProjectTabKey(key);

  const getEvents = () => {
    if (eventStore.events) {
      const events = eventStore.events.filter((el: CalendarEvent) =>
        (el.startDate?.format("YYYY-MM-DD") >= timeReportStore.startDate.format("YYYY-MM-DD") &&
          el.startDate?.format("YYYY-MM-DD") <= timeReportStore.endDate.format("YYYY-MM-DD")) ||
        (el.endDate?.format("YYYY-MM-DD") >= timeReportStore.startDate.format("YYYY-MM-DD") &&
          el.endDate?.format("YYYY-MM-DD") <= timeReportStore.endDate.format("YYYY-MM-DD"))
      );
      const birthDays = eventStore.birthdays.filter((el: CalendarEvent) =>
        (el.startDate?.format("MM-DD") >= timeReportStore.startDate.format("MM-DD") &&
          el.startDate?.format("MM-DD") <= timeReportStore.endDate.format("MM-DD")) ||
        (el.endDate?.format("MM-DD") >= timeReportStore.startDate.format("MM-DD") &&
          el.endDate?.format("MM-DD") <= timeReportStore.endDate.format("MM-DD"))
      );
      setEvents([...events, ...birthDays]);
    }
  }

  const deleteTime = async (id: any) => {
    try {
      message.loading({ content: "Deleting...", key: "deleting" });

      await timeReportStore.deleteById(id);

      await timeReportStore.getMyTime(applicationStore.user.id);

      if (applicationStore.user?.roles?.some(r => r.id === Roles.Owner || r.id === Roles.Financier)) {
        await timeReportStore.getForFinancier(true);
      }

      update();

      message.success("The time was deleted");
    } catch (error) {
      message.error("Failed to delete time report");
      console.error(error);
    } finally {
      message.destroy("deleting");
    }
  }

  useEffect(() => {
    timeReportStore.getAllByPeriod(true).then(() => {
      update();
    });
  }, [timeReportStore.startDate, timeReportStore.endDate]);

  useEffect(() => {
    employeesStore.getUsers().then(res => {
      projectsStore.getAll();
      timeReportStore.getAllByPeriod(true);
      timeReportStore.getMyTime(applicationStore.user.id);
      eventStore.getBirthdays(employeesStore.users);

      if ((applicationStore.user?.roles?.some(r => r.id === Roles.Owner || r.id === Roles.Financier))) {
        timeReportStore.getForFinancier(true);
      }
    });
  }, []);

  useEffect(() => {
    update();
  }, [timeReportStore.reportsForFinancier, timeReportStore.reports, refresh])

  const update = () => {
    timeReportStore.updateGroups(applicationStore.user.id, employeesStore, projectsStore);
  }

  useEffect(() => {
    getEvents();
  }, [eventStore.events, eventStore.birthdays, timeReportStore.startDate, timeReportStore.endDate])

  const applyTabChange = useCallback((key: string) => {
    if (key === 'allUsers') {
      if (activeUserTabKey === "vacationSickLeave") setFullYearRange();
      else resetToCurrentMonth();
    } else if (key === 'allProjects') {
      resetToCurrentMonth();
    } else {
      resetToCurrentMonth();
    }

    switch (key) {
      case "my":
        timeReportStore.getMyTime(applicationStore.user.id);
        setIsAllTimeChecked(false);
        break;
      case "allUsers":
      case "allProjects":
        timeReportStore.getAllByPeriod();
        setIsAllTimeChecked(false);
        break;
      case "Events":
      case "Calendar":
        if (employeesStore.users.length > 0) {
          eventStore.getAll();
          eventStore.getBirthdays(employeesStore.users);
        } else {
          employeesStore.getUsers().then(users => {
            eventStore.getBirthdays(users);
            eventStore.getAll();
          })
        }
        setIsAllTimeChecked(false);
        break;
      case "vacationSickLeave":
        eventStore.getAll();
        setFullYearRange();
        break;
    }
  }, [activeUserTabKey, employeesStore, eventStore, timeReportStore, applicationStore.user?.id]);

  useEffect(() => {
    if (!tabKey) {
      if (tenant) navigate(`/${tenant}/time-reports/my`, { replace: true });
      return;
    }
    applyTabChange(tabKey);
  }, [tabKey, tenant, navigate, applyTabChange]);

  const handleTabChange = (key: string) => {
    if (!tenant) return;
    navigate(`/${tenant}/time-reports/${key}`);
  };

  const currentKey = tabKey ?? 'my';

  return (
    <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={t("Events")}>
      <div className="reports-font-settings">
        <div className={timeReportStore.isFull ? "" : "container"} style={{ margin: "20px auto" }}>
          <Flex justify="end">
            <Flex gap={5} align="baseline">
              <Button shape="circle" onClick={() => {
                const prevStartDate = timeReportStore.startDate.clone();
                timeReportStore.setStartDate(prevStartDate.subtract(1, "month").startOf("month").local())
                timeReportStore.setEndDate(prevStartDate.subtract(1, "month").endOf("month").local())
              }}>
                <LeftOutlined />
              </Button>
              <RangePicker
                value={[timeReportStore.startDate, timeReportStore.endDate]}
                picker="date"
                format="DD MMMM YYYY"
                style={{ marginBottom: 16 }}
                onChange={(dates: any) => {
                  timeReportStore.setStartDate(dates[0].local())
                  timeReportStore.setEndDate(dates[1].local())
                }}
              />
              <Button shape="circle" onClick={() => {
                const prevStartDate = timeReportStore.startDate.clone();
                timeReportStore.setStartDate(prevStartDate.add(1, "month").startOf("month").local())
                timeReportStore.setEndDate(prevStartDate.add(1, "month").endOf("month").local())
              }}>
                <RightOutlined />
              </Button>
            </Flex>
          </Flex>

          <Tabs activeKey={currentKey} onChange={handleTabChange}>
            <TabPane tab={t('TimeReports.MyTime')} key="my">
              <MyTime
                setLogUserId={setLogUserId}
                setLogProjectId={setLogProjectId}
                showModalAdd={showModal}
                showModalEdit={showModalEdit}
                deleteTime={deleteTime}
                setIsChangeUser={setIsChangeUser}
                setEditTimeReport={setEditTimeReport}
                timeReports={timeReportStore.timeByMe as TimeReport[] | { log: TimeReport; payment: number }[]}
                onRefresh={() => {
                  timeReportStore.getAllByPeriod(true).then(() => {
                    timeReportStore.getMyTime(applicationStore.user.id).then(update);
                  });
                }}
              />
            </TabPane>

            <TabPane tab={t('TimeReports.UserActivity')} key="allUsers">
              <ByUser
                setLogUserId={setLogUserId}
                setLogProjectId={setLogProjectId}
                showModalAdd={showModal}
                showModalEdit={showModalEdit}
                deleteTime={deleteTime}
                setIsChangeUser={setIsChangeUser}
                setEditTimeReport={setEditTimeReport}
                timeReports={timeReportStore.timeByUsers}
                startDate={timeReportStore.startDate}
                endDate={timeReportStore.endDate}
                setStartDate={timeReportStore.setStartDate}
                setEndDate={timeReportStore.setEndDate}
                resetToCurrentMonth={resetToCurrentMonth}
                setFullYearRange={setFullYearRange}
                activeUserTabKey={activeUserTabKey}
                onUserTabChange={handleUserTabChange}
                isAllTimeChecked={isAllTimeChecked}
                setIsAllTimeChecked={setIsAllTimeChecked}
              />
            </TabPane>

            <TabPane tab={t('TimeReports.ProjectActivity')} key="allProjects">
              <ProjectsTimeReports
                setLogUserId={setLogUserId}
                setLogProjectId={setLogProjectId}
                showModalAdd={showModal}
                showModalEdit={showModalEdit}
                deleteTime={deleteTime}
                setIsChangeUser={setIsChangeUser}
                setEditTimeReport={setEditTimeReport}
                timeReports={timeReportStore.timeByProjects}
                startDate={timeReportStore.startDate}
                endDate={timeReportStore.endDate}
                setStartDate={timeReportStore.setStartDate}
                setEndDate={timeReportStore.setEndDate}
                activeProjectTabKey={activeProjectTabKey}
                onProjectTabChange={handleProjectTabChange}
              />
            </TabPane>

            <TabPane tab={t('TimeReports.Events')} key="Events">
              <EventPage events={events} />
            </TabPane>

            {isChief && (
              <TabPane tab={t('TimeReports.Tasks')} key="Tasks">
                <UserTaskPage />
              </TabPane>
            )}

            <TabPane tab={t('TimeReports.Calendar')} key="Calendar">
              <EnhancedCalendar
                startDate={timeReportStore.startDate}
                endDate={timeReportStore.endDate}
                setStartDate={timeReportStore.setStartDate}
                setEndDate={timeReportStore.setEndDate}
              />
            </TabPane>
          </Tabs>
        </div>
      </div>


      <AddTimeReport
        isModalOpen={isModalOpen}
        hideModal={hideModal}
        projectId={logProjectId}
        userId={logUserId}
        isChangeUser={isChangeUser}
        refresh={refresh}
        setRefresh={setRefresh}
      />

      {editTimeReport && (
        <EditTimeReport
          isModalOpen={isModalOpenEdit}
          hideModal={hideModalEdit}
          id={editTimeReport?.id}
          time={editTimeReport?.totalTime}
          endTime={editTimeReport.endTime}
          description={editTimeReport?.description}
        />
      )}
    </Layout>
  )
});

export default TimeReports;
